import { EditTileGeneralFields } from "client/Features/Workspace/LeftSliders/EditTile/GeneralFields";
import {
	Checkbox,
	ComboBoxIntermediateState,
	Form,
	FormItem,
	Select,
	SelectedComboBoxItem,
	SelectOption,
	useComboBoxIntermediateState,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { EditTileHandle } from "client/Features/Workspace/LeftSliders/EditTile/EditTile";
import { ExistingTile } from "../common";
import {
	AddTilesResponseModel,
	HubUpdateTileModel,
	HubAppDetailsTileModel,
	updateHubItemTile,
	HubTileDefaultViewConfigurationSaveModel,
	HubTileDefaultViewKind,
	SaveHubTileLayoutModel,
	DetailsHubTileLayout,
	HubTileDefaultViewConfigurationClientModel
} from "client/Api/Workspace/workspace";
import {
	createValidationConfiguration,
	IValidationConfiguration
} from "client/Validation";
import { UpdateTileFormData, useUpdateTile } from "client/Features/Workspace/LeftSliders/EditTile/hooks/useUpdateTile";
import { IFormHandler, useFormHandler } from "client/Forms";
import { EditTileFromData, initialGenericState } from "client/Features/Workspace/LeftSliders/EditTile/EditGenericTile";
import { HubTileLayoutKind } from "client/Api/Hub/common";
import { useUserTimezone } from "client/Hooks";
import {
	doesNotExceedLength,
	hasMinimumEntries,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { never } from "client_legacy/ndaw/errors";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { EMPTY_ARRAY, isNotNil } from "client/Misc/utils";
import { IValueInputAttachable } from "client/Forms/attachable";
import { HubDataSource } from "client/Api/Hub/hub";
import {
	hubDataSourceToHubItemKindMap,
	hubItemKindToHubDataSourceMap,
	isHubItemDataSource
} from "client/Features/Hub/Sidebar/common";
import { HubItemFormSection, isHubItemLayout } from "./HubItemFormSection";
import { TimeZone } from "client/DateAndTime/timezones";

export type HubTileDefaultViewConfigurationFormModel = HubTileDefaultViewConfigurationSaveModel & {
	kind: HubTileDefaultViewKind;
	categoryId: number | null;
	categoryIds: readonly number[];
};

export type SaveHubTileLayoutFormModel = Pick<SaveHubTileLayoutModel, "kind"> & {
	itemKind: HubItemKind;
	itemId: number | null;
	defaultView: HubTileDefaultViewConfigurationFormModel;
	isAutoPlayEnabled: boolean;
};

type HubEditFormData = EditTileFromData<
	OverrideProperties<
		HubUpdateTileModel,
		{
			source: HubDataSource;
			layout: SaveHubTileLayoutFormModel;
		}
	>
>;

export type EditHubTileProps = {
	isLoading: boolean;
	onIsLoadingChange: (value: boolean) => void;
	initialModel: HubAppDetailsTileModel;
	mode: ExistingTile;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditHubTile = forwardRef(
	(props: EditHubTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			initialModel,
			mode,
			onAfterSave,
			onIsLoadingChange,
			isLoading
		} = props;

		const { isCommunitiesFeatureAvailable } = initialModel;

		const [formData, setFormData] = useState<HubEditFormData>(getInitialFormData(initialModel));
		const [isSelectedSpotlightItemNotAvailable, setIsSelectedSpotlightItemNotAvailable] = useState<boolean>(
			getInitialIsSelectedSpotlightItemNotAvailable(initialModel)
		);

		const isLiveTile: boolean = formData.dimension.columnSpan >= 4
			&& formData.dimension.rowSpan >= 4;

		const formHandler: IFormHandler<HubEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const userTimezone: TimeZone = useUserTimezone();

		const {
			layout: initialLayout
		} = initialModel;

		const spotlightInitialItem = useMemo(
			(): SelectedComboBoxItem<number> | undefined => {
				if (initialLayout.kind === HubTileLayoutKind.Spotlight && initialLayout.item) {
					const { item } = initialLayout;
					return {
						value: item.id,
						text: item.title
					};
				}

				return undefined;
			},
			[initialLayout]
		);

		const hubSourceOptions = useMemo(
			(): readonly SelectOption<HubDataSource>[] => {
				let result: readonly SelectOption<HubDataSource>[] = [
					{
						value: HubDataSource.Announcement,
						text: localize("_Announcements")
					},
					{
						value: HubDataSource.Community,
						text: localize("_Communities")
					},
					{
						value: HubDataSource.Event,
						text: localize("_Events")
					},
					{
						value: HubDataSource.KnowledgeItem,
						text: localize("_KnowledgeBase")
					}
				];

				if (!isCommunitiesFeatureAvailable) {
					result = result.filter((x) => x.value !== HubDataSource.Community);
				}

				return result;
			},
			[isCommunitiesFeatureAvailable]
		);

		const spotlightItemViewState: ComboBoxIntermediateState<number> = useComboBoxIntermediateState<number>(
			spotlightInitialItem
		);

		useEffect(
			(): void => {
				if (!isSelectedSpotlightItemNotAvailable) {
					return;
				}

				const layout: SaveHubTileLayoutFormModel = formData.layout;

				if (layout.kind !== HubTileLayoutKind.Spotlight
					|| isNotNil(layout.itemId)
				) {
					setIsSelectedSpotlightItemNotAvailable(false);
				}
			},
			[
				formData.layout,
				isSelectedSpotlightItemNotAvailable
			]
		);

		const {
			updateHandler
		} = useUpdateTile<HubUpdateTileModel>(
			formHandler,
			updateHubItemTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave,
			prepareForSave
		);

		useImperativeHandle(
			ref,
			() => ({ updateHandler })
		);

		const sourceChangedHandler = useCallback(
			(source: HubDataSource): void => {
				if (formData.source === source) {
					return;
				}

				spotlightItemViewState.onSelectedItemChange(undefined);

				let layout: SaveHubTileLayoutFormModel;

				switch (source) {
					case HubDataSource.Announcement:
					case HubDataSource.KnowledgeItem:
					case HubDataSource.Event:
						layout = {
							...formData.layout,
							itemKind: hubDataSourceToHubItemKindMap[source],
							kind: isHubItemDataSource(formData.source)
								? formData.layout
									.kind
								: HubTileLayoutKind.List,
							itemId: null,
							defaultView: {
								kind: HubTileDefaultViewKind.AllItems,
								categoryId: null,
								categoryIds: []
							}
						};
						break;
					case HubDataSource.Community:
						layout = {
							...formData.layout,
							kind: HubTileLayoutKind.Feed
						};

						break;
					default:
						never(source);
				}

				const layoutAttachable: IValueInputAttachable<SaveHubTileLayoutFormModel> = formHandler.for("layout");

				layoutAttachable
					.reducer
					.setValue(layout);

				layoutAttachable
					.validation
					.triggerInputFinished();
			},
			[
				formData,
				formHandler,
				spotlightItemViewState
			]
		);

		return (
			<Form
				onSubmit="NONE"
			>
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>

				{isLiveTile && (
					<>
						<FormItem label={localize("_ItemType")}>
							<Select
								{...formHandler
									.for("source")
									.attachToSelect()
									.withOnChange(sourceChangedHandler)
								}
								options={hubSourceOptions}
								menuTitle={localize("_ItemType")}
							/>
							<ValidationError {...formHandler.for("source").attachToValidationError()} />
						</FormItem>

						{formHandler.when(
							(x) => isHubItemLayout(x.layout.kind),
							(x) => (
								<HubItemFormSection
									attachable={x.for("layout")}
									spotlightItemState={spotlightItemViewState}
									isSelectedItemNotAvailable={isSelectedSpotlightItemNotAvailable}
									timezone={userTimezone}
								/>
							)
						)}

						<FormItem>
							<Checkbox
								{...formHandler.for("hideHeader").attachToCheckbox()}
								text={localize("_HideTileHeader")}
							/>
						</FormItem>
						<FormItem>
							<Checkbox
								{...formHandler.for("hideFooter").attachToCheckbox()}
								text={localize("_HideTileFooter")}
							/>
						</FormItem>
					</>
				)}
			</Form>
		);
	}
);

EditHubTile.displayName = "EditHubTile";

function getInitialFormData(
	initialModel: HubAppDetailsTileModel
): HubEditFormData {
	const {
		layout,
		hideHeader,
		hideFooter
	} = initialModel;

	let source: HubDataSource;

	switch (layout.kind) {
		case HubTileLayoutKind.Carousel:
		case HubTileLayoutKind.Grid:
		case HubTileLayoutKind.List:
		case HubTileLayoutKind.Spotlight:
			source = hubItemKindToHubDataSourceMap[layout.itemKind];
			break;
		case HubTileLayoutKind.Feed:
			source = HubDataSource.Community;
			break;
		default:
			never(layout);
	}

	return {
		...initialGenericState(initialModel),
		source,
		layout: getInitialLayoutFormData(layout),
		hideHeader,
		hideFooter
	};
}

function getInitialIsSelectedSpotlightItemNotAvailable(initialModel: HubAppDetailsTileModel): boolean {
	const {
		layout
	} = initialModel;

	return layout.kind === HubTileLayoutKind.Spotlight
		&& !layout.item;
}

const allItemsDefaultViewFormModel: HubTileDefaultViewConfigurationFormModel = {
	kind: HubTileDefaultViewKind.AllItems,
	categoryId: null,
	categoryIds: EMPTY_ARRAY,
};

const defaultFormModel: SaveHubTileLayoutFormModel = {
	kind: HubTileLayoutKind.List,
	defaultView: allItemsDefaultViewFormModel,
	isAutoPlayEnabled: false,
	itemId: null,
	itemKind: HubItemKind.Announcement,
};

function getInitialLayoutFormData(
	initialLayout: DetailsHubTileLayout
): SaveHubTileLayoutFormModel {
	switch (initialLayout.kind) {
		case HubTileLayoutKind.Carousel:
		case HubTileLayoutKind.Grid:
		case HubTileLayoutKind.List:
			return {
				...defaultFormModel,
				itemKind: initialLayout.itemKind,
				kind: initialLayout.kind,
				defaultView: getInitialDefaultViewFormModel(initialLayout.defaultView),
				isAutoPlayEnabled: initialLayout.kind === HubTileLayoutKind.Carousel
					? initialLayout.isAutoPlayEnabled
					: defaultFormModel.isAutoPlayEnabled,
			};
		case HubTileLayoutKind.Spotlight:
			return {
				...defaultFormModel,
				kind: initialLayout.kind,
				itemKind: initialLayout.itemKind,
				itemId: initialLayout.item
					?.id ?? null,
			};
		case HubTileLayoutKind.Feed:
			return {
				...defaultFormModel,
				kind: initialLayout.kind
			};
		default:
			never(initialLayout);
	}
}

function getInitialDefaultViewFormModel(
	configuration: HubTileDefaultViewConfigurationClientModel
): HubTileDefaultViewConfigurationFormModel {
	switch (configuration.kind) {
		case HubTileDefaultViewKind.AllItems:
			return allItemsDefaultViewFormModel;
		case HubTileDefaultViewKind.Category:
			return {
				kind: configuration.kind,
				categoryId: configuration.categoryId,
				categoryIds: EMPTY_ARRAY,
			};
		case HubTileDefaultViewKind.CategoryCollection:
			return {
				kind: configuration.kind,
				categoryId: null,
				categoryIds: configuration.categoryIds,
			};
		default:
			never(configuration);
	}
}

function prepareForSave(
	formData: UpdateTileFormData<HubUpdateTileModel>
): UpdateTileFormData<HubUpdateTileModel> {
	const { columnSpan, rowSpan } = formData.dimension;
	const isLiveTile: boolean = columnSpan >= 4 && rowSpan >= 4;

	return {
		...formData,
		layout: isLiveTile
			// Our current tile saving infrastructure doesn't support having an extended model of
			// the api contract, so we need an hard cast here to clean it up
			? prepareLayoutForSaving(formData.layout as SaveHubTileLayoutFormModel)
			: null
	};
}

function prepareLayoutForSaving(
	layout: SaveHubTileLayoutFormModel
): SaveHubTileLayoutModel {
	switch (layout.kind) {
		case HubTileLayoutKind.Carousel:
			return {
				kind: layout.kind,
				itemKind: layout.itemKind,
				defaultView: prepareDefaultViewForSaving(layout.defaultView),
				isAutoPlayEnabled: layout.isAutoPlayEnabled
			};
		case HubTileLayoutKind.Grid:
		case HubTileLayoutKind.List:
			return {
				kind: layout.kind,
				itemKind: layout.itemKind,
				defaultView: prepareDefaultViewForSaving(layout.defaultView)
			};
		case HubTileLayoutKind.Spotlight:
			return {
				kind: layout.kind,
				itemKind: layout.itemKind,
				itemId: layout.itemId
			};
		case HubTileLayoutKind.Feed:
			return {
				kind: layout.kind
			};
		default:
			never(layout.kind);
	}
}

function prepareDefaultViewForSaving(
	configuration: HubTileDefaultViewConfigurationFormModel
): HubTileDefaultViewConfigurationSaveModel {
	const {
		kind,
		categoryId,
		categoryIds
	} = configuration;

	switch (kind) {
		case HubTileDefaultViewKind.AllItems:
			return { kind };
		case HubTileDefaultViewKind.Category:
			return { kind, categoryId };
		case HubTileDefaultViewKind.CategoryCollection:
			return { kind, categoryIds };
		default:
			never(configuration);
	}
}

const defaultViewValidation: IValidationConfiguration<HubTileDefaultViewConfigurationFormModel> =
	createValidationConfiguration<HubTileDefaultViewConfigurationFormModel>();

defaultViewValidation.when(x => x.kind === HubTileDefaultViewKind.Category)
	.forScalar("categoryId")
	.add(
		isNotNullNorUndefined(),
	);

defaultViewValidation.when(x => x.kind === HubTileDefaultViewKind.CategoryCollection)
	.forCollection("categoryIds")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		hasMinimumEntries(2)
	);

const layoutValidation: IValidationConfiguration<SaveHubTileLayoutFormModel> = createValidationConfiguration<SaveHubTileLayoutFormModel>();

layoutValidation
	.when(
		(x) => x.kind === HubTileLayoutKind.Carousel
			|| x.kind === HubTileLayoutKind.Grid
			|| x.kind === HubTileLayoutKind.List
	)
	.forObject("defaultView")
	.addValidators(defaultViewValidation);

layoutValidation.when((x) => x.kind === HubTileLayoutKind.Spotlight)
	.forScalar("itemId")
	.add(
		isNotNullNorUndefined()
	);

const validationBuilder: IValidationConfiguration<HubEditFormData> =
	createValidationConfiguration<HubEditFormData>();

validationBuilder
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationBuilder
	.when(
		(x) => x.dimension.columnSpan >= 4
			&& x.dimension.rowSpan >= 4
	)
	.forObject("layout")
	.addValidators(layoutValidation);
